import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, Search, SortAsc, SortDesc, Filter, AlertTriangle } from 'lucide-react';
import Header from './Header';
import { getUserInfo } from './apiUtils.js';
import CreateProductDialog from './CreateProductDialog.js';
import ManageUserAccessDialog from './ManageUserAccessDialog';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '700px',
    maxWidth: '90vw',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxHeight: '90vh',
    overflowY: 'auto'
  }
};

const Dialog = ({ message, onClose, onCancel, isConfirmation, isSuccess }) => {
  const isEmptyProducts = message.startsWith('No Products found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    dialog: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: isEmptyProducts ? '16px' : '8px',
      width: '500px',
      maxWidth: '90vw',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      border: isEmptyProducts ? '1px solid #ccc' : 'none',
    },
    title: {
      fontSize: isEmptyProducts ? '16.8px' : '18px',
      lineHeight: isEmptyProducts ? '1.4' : '1.2',
      textAlign: 'center',
      margin: 0,
      color: '#000',
      backgroundColor: isConfirmation ? 'white' : 'white'
    },
    button: {
      backgroundColor: '#FFFDD0',
      border: 'none',
      borderRadius: '4px',
      padding: '10px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 500,
      color: 'black'
    }
  };

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        <h2 style={dialogStyles.title}>
          {formattedMessage}
        </h2>

        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...dialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...dialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...dialogStyles.button,
              width: isEmptyProducts ? '120px' : '100%',
              margin: isEmptyProducts ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const styles = {
  deleteButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '6px 12px',
    height: '28px',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  editButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '6px 12px',
    height: '28px',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  disabled: {
    opacity: 0.6
  },
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '0px',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    fontSize: '0.875rem',
  },
  th: {
    backgroundColor: '#E5E4E2',
    color: '#0064e5',
    padding: '6px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontSize: '0.875rem',
    lineHeight: '1',
    height: '32px',
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
  },
  checkbox: {
    marginRight: '10px',
  },
  button: {
    padding: '6px 12px',
    margin: '0 5px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  error: {
    color: 'red',
    padding: '10px',
    marginBottom: '10px',
  },
};

const Products = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogType, setDialogType] = useState('info'); // 'info', 'confirm', 'success'
  const [redirectPath, setRedirectPath] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProductForEdit, setSelectedProductForEdit] = useState(null);
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Updated permissions structure to match IntegrationSystems
  const [permissions, setPermissions] = useState({
    canDelete: false,
    canEdit: false,
    canCreate: false,
    canView: true,
    canManageUserAccess: false,
    canRemoveUserAccess: false
  });

  // Add error state similar to BusinessTeams
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const login_token = localStorage.getItem('authToken');
  const [products, setProducts] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');

  const [isUserAccessDialogOpen, setIsUserAccessDialogOpen] = useState(false);
  const [selectedProductForAccess, setSelectedProductForAccess] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete confirmation dialog state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const handleManageUserAccess = (productId) => {
    const productForAccess = products.find(product => product.product_id === productId);

    if (productForAccess) {
      setSelectedProductForAccess(productForAccess);
      setIsUserAccessDialogOpen(true);
    }
  };

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to products
  const sortedProducts = React.useMemo(() => {
    let sortableProducts = [...filteredProducts];
    if (sortConfig.key) {
      sortableProducts.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableProducts;
  }, [filteredProducts, sortConfig]);

  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setDialogType('info');
    setRedirectPath(path);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    if (redirectPath) {
      window.location.href = redirectPath;
    }
    if (pendingDeleteAction) {
      pendingDeleteAction();
      setPendingDeleteAction(null);
    }
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
    setPendingDeleteAction(null);
  };

  const handleCreateProduct = () => {
    // Immediately refresh the products list
    fetchProducts();
  };

  // Handle edit success separately to avoid the double delay
  const handleEditSuccess = () => {
    // Immediately close the dialog and refresh data
    setIsCreateDialogOpen(false);
    setIsEditMode(false);
    setSelectedProductForEdit(null);
    fetchProducts();

    // Show our own success message
    setSuccessMessage('Product updated successfully!');
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };

  const handleSearch = () => {
    const filtered = products.filter(product =>
      product.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.product_description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.product_company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.product_version.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleSearchInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // If the input is cleared (empty), reset to show all products
    if (newValue === '') {
      setFilteredProducts(products);
    }
  };

  const handleEdit = (productId) => {
    const productToEdit = products.find(product => product.product_id === productId);

    if (productToEdit) {
      setSelectedProductForEdit(productToEdit);
      setIsEditMode(true);
      setIsCreateDialogOpen(true);
    }
  };

  // Updated delete function to show confirmation dialog first
  const handleDelete = async (productId) => {
    const product = products.find(p => p.product_id === productId);
    setProductToDelete(product);
    setIsDeleteConfirmOpen(true);
  };

  // Actual delete function (called after confirmation)
  const handleDeleteConfirmed = async () => {
    setIsDeleteConfirmOpen(false);
    setIsDeleting(true);
    setDeleteError(null);
    setError(null);
    setSuccessMessage('');

    try {
      const response = await fetch(appConfig.getUrl('/delete_product'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          product_ids: [productToDelete.product_id]
        }),
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setSuccessMessage('Product deleted successfully!');
        setSelectedRows(new Set());
        fetchProducts();

        // Clear success message after delay
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(data.status_description || 'Failed to delete product');
      }
    } catch (err) {
      setError('Failed to delete product: ' + err.message);
    } finally {
      setIsDeleting(false);
      setProductToDelete(null);
    }
  };

  // Delete confirmation dialog component
  const renderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmOpen || !productToDelete) return null;

    return (
      <div style={{...DialogStyles.overlay, zIndex: 1100}}>
        <div style={{...DialogStyles.dialog, width: '500px'}}>
          <div style={{
            backgroundColor: '#FFE6E6',
            color: '#CC0000',
            padding: '15px 0',
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            borderRadius: '8px 8px 0 0',
            margin: '-20px -20px 20px -20px'
          }}>
            <div className="flex items-center justify-center gap-2">
              <AlertTriangle size={20} />
              <span>Confirm Deletion</span>
            </div>
          </div>

          <div className="px-6 py-4">
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete this product?
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            {/* Show product details */}
            <div className="bg-gray-50 rounded p-3 mb-4">
              <p className="text-xs text-gray-600 mb-2">Product details:</p>
              <div className="text-sm text-gray-700">
                <div><strong>Name:</strong> {productToDelete.product_name}</div>
                <div><strong>Company:</strong> {productToDelete.product_company}</div>
                <div><strong>Version:</strong> {productToDelete.product_version}</div>
                {productToDelete.product_description && (
                  <div><strong>Description:</strong> {productToDelete.product_description}</div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-4 p-6 pt-0">
            <button
              onClick={() => {
                setIsDeleteConfirmOpen(false);
                setProductToDelete(null);
              }}
              style={{
                backgroundColor: '#f8f9fa',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirmed}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Delete Product
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Updated permission checking function to match IntegrationSystems
  const checkPermission = async (categoryHeader) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: []
        }),
      });

      const data = await response.json();

      // Create new permissions object
      const newPermissions = {
        canDelete: false,
        canEdit: false,
        canCreate: false,
        canView: true,
        canManageUserAccess: false,
        canRemoveUserAccess: false
      };

      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
      console.log('User Action:', item.user_action, 'Access:', item.has_access);

        switch(item.user_action) {
          case 'Create Or Edit Products':
            newPermissions.canCreate = item.has_access;
            newPermissions.canEdit = item.has_access;
            break;
          case 'Delete Products':
            newPermissions.canDelete = item.has_access;
            break;
          case 'Add Or Edit User Access To Products':
            newPermissions.canManageUserAccess = item.has_access;
            break;
          case 'Remove User Access To Products':
            newPermissions.canRemoveUserAccess = item.has_access;
            break;
        }
      });

      setPermissions(newPermissions);
      console.log('Product Permissions:', newPermissions);

      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return false;
    }
  };

  useEffect(() => {
    if (corporateAccount) {
      fetchProducts();
    }
  }, [corporateAccount, currentProject]);

  useEffect(() => {
    if (searchQuery === '') {
      fetchProducts();
    }
  }, [searchQuery]);

  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  // Updated permission checking useEffect to match IntegrationSystems
  useEffect(() => {
    if (corporateAccount && userId && currentProject) {
      checkPermission('Products Definition');
    }
  }, [corporateAccount, userId, currentProject]);

  useEffect(() => {
    const setInitialLoad = async () => {
      const current_project = localStorage.getItem('current_project');
      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }
      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          if (result.userInfo.default_project == 'null' && current_project == 'null') {
            showErrorDialog('Please select a project on the home page', '/RequirementsPortal');
            return;
          }
          setCorporateAccount(result.userInfo.corporate_account);
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setAccountDescription(result.userInfo.account_description);
          setCurrentProject(current_project === 'null' ? result.userInfo.default_project : current_project);
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);

  const fetchProducts = async () => {
    setSelectedRows(new Set());

    if (!currentProject) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_product_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          status: "Active"
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      if (data.status === 'Success') {
        setProducts(data.product_list);
      } else {
        setProducts([]);
        if (data.product_list && data.product_list.length === 0) {
          setDialogMessage('No Products found to load.\nCreate new products to get started');
          setShowDialog(true);
        } else {
          throw new Error(data.status_description || 'Failed to fetch products');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === paginatedProducts.length) {
      setSelectedRows(new Set());
    } else {
      const newSelected = new Set(paginatedProducts.map(product => product.product_id));
      setSelectedRows(newSelected);
    }
  };

  const toggleRowSelection = (productId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedRows(newSelected);
  };

  // Calculate pagination
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Header />

      <div className="mt-12">

        {/* Title bar matching RaidLogManagement */}
        <h1 className="text-l font-bold text-blue-800 mr-20 text-center">Manage Products</h1>

        <div className="flex items-center mb-4 justify-center bg-blue-100 p-3 rounded">
          <div className="flex justify-between items-center" style={{ width: '60%' }}>
            {/* Search bar */}
            <div className="flex items-center">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                placeholder="Search products..."
                className="p-2 pr-10 border rounded w-full"
              />
              <button
                onClick={handleSearch}
                className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Search size={18} />
              </button>
            </div>

            <h2 className="text-l font-bold text-blue-700 text-center">Project: {currentProject}</h2>

            {/* Updated Create New button with tooltip */}
            <div className="group relative">
              <button
                disabled={!permissions.canCreate}
                onClick={() => {
                  if (permissions.canCreate) {
                    setIsEditMode(false);
                    setSelectedProductForEdit(null);
                    setIsCreateDialogOpen(true);
                  }
                }}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!permissions.canCreate ? 'opacity-50' : ''}`}
              >
                <Plus size={18} className="mr-2" />
                Create New
              </button>

              {/* Tooltip for disabled state */}
              {!permissions.canCreate && (
                <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                  Insufficient permission
                  <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-blue-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search and Create New button row */}
        <div className="flex justify-center mb-4">
          <div className="flex justify-between items-center" style={{ width: '60%' }}>
          </div>
        </div>

        {/* Success/Error Messages - aligned to left edge of table */}
        <div className="flex justify-center mb-4">
          <div style={{ width: '60%' }}>
            {successMessage && (
              <div className="mb-4 p-2 rounded bg-green-50 text-green-700">
                {successMessage}
              </div>
            )}

            {deleteError && (
              <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
                {deleteError}
              </div>
            )}

            {error && (
              <div className={`mb-4 p-2 rounded ${
                error.includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              }`}>
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Main Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden" style={{ width: '60%', margin: '0 auto' }}>
          <table style={styles.table}>
            <thead className="bg-gray-200">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('product_name')}
                >
                  <div className="flex items-center">
                    <span className="underline">Product Name</span>
                    <span className="ml-1">
                      {sortConfig.key === 'product_name' ? (
                        sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                      ) : null}
                    </span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('product_company')}
                >
                  <div className="flex items-center">
                    <span className="underline">Company</span>
                    <span className="ml-1">
                      {sortConfig.key === 'product_company' ? (
                        sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                      ) : null}
                    </span>
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-24"
                  onClick={() => handleSort('product_version')}
                >
                  <div className="flex items-center">
                    <span className="underline">Version</span>
                    <span className="ml-1">
                      {sortConfig.key === 'product_version' ? (
                        sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                      ) : null}
                    </span>
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-blue-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map(product => (
                <tr key={product.product_id} className="border-t hover:bg-blue-50">



                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium">{product.product_name}</div>
                      <div className="text-sm text-gray-500">{product.product_description}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{product.product_company}</td>
                  <td className="px-4 py-3">{product.product_version}</td>

                <td className="px-4 py-3">
                  <div className="flex justify-center gap-2">
                    {/* Edit button with tooltip */}
                    <div className="group relative">
                      <button
                        className={`p-2 rounded ${
                          permissions.canEdit
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-800 '
                        }`}
                        disabled={!permissions.canEdit}
                        onClick={() => {
                          if (permissions.canEdit) {
                            handleEdit(product.product_id);
                          }
                        }}
                        title="Edit Product"
                      >
                        <img
                          src="/icons/noun-edit-2162449-007435.svg"
                          alt="Edit Product"
                          className={`h-5 w-5 ${!permissions.canEdit ? 'opacity-60 grayscale' : ''}`}
                        />
                      </button>

                      {/* Edit tooltip */}
                      {!permissions.canEdit && (
                        <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                          Insufficient permission
                          <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                        </div>
                      )}
                    </div>

                    {/* Delete button with tooltip */}
                    <div className="group relative">
                      <button
                        className={`p-2 rounded ${
                          permissions.canDelete
                            ? 'text-red-600 hover:bg-red-50'
                            : 'text-gray-800 '
                        }`}
                        disabled={!permissions.canDelete || isDeleting}
                        onClick={() => {
                          if (permissions.canDelete) {
                            handleDelete(product.product_id);
                          }
                        }}
                        title={isDeleting ? 'Deleting...' : 'Delete Product'}
                      >
                        <img
                          src="/icons/noun-delete-1560851-FF001C.svg"
                          alt="Delete Product"
                          className={`h-5 w-5 ${!permissions.canDelete ? 'opacity-60 grayscale' : ''}`}
                        />
                      </button>

                      {/* Delete tooltip */}
                      {!permissions.canDelete && (
                        <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                          Insufficient permission
                          <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                        </div>
                      )}
                    </div>

                    {/* Manage User Access button with tooltip */}
                    <div className="group relative">
                      <button
                        className={`p-2 rounded ${
                          permissions.canManageUserAccess
                            ? 'hover:bg-blue-50'
                            : 'opacity-50 '
                        }`}
                        disabled={!permissions.canManageUserAccess}
                        onClick={() => {
                          if (permissions.canManageUserAccess) {
                            handleManageUserAccess(product.product_id);
                          }
                        }}
                        title="Manage User Access"
                      >
                        <img
                          src="/icons/noun-user-access-6255731-FF59B9.svg"
                          alt="Manage User Access"
                          className={`h-10 w-10 ${!permissions.canManageUserAccess ? 'opacity-90 grayscale' : ''}`}
                        />
                      </button>

                      {/* Manage User Access tooltip */}
                      {!permissions.canManageUserAccess && (
                        <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                          Insufficient permission
                          <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                        </div>
                      )}
                    </div>




                  </div>
                                  </td>





                </tr>
              ))}
              {sortedProducts.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    No products found. Create a new product or adjust your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination controls matching RaidLogManagement */}
        {!loading && sortedProducts.length > 0 && (
          <div className="mt-4 flex justify-center">
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow" style={{ width: '60%' }}>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Total Products: {sortedProducts.length}</span>
                <span>| Showing {sortedProducts.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedProducts.length)} of {sortedProducts.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  aria-label="Next page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  className="border rounded p-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={handleDialogClose}
          onCancel={handleDialogCancel}
          isConfirmation={dialogType === 'confirm'}
          isSuccess={dialogType === 'success'}
        />
      )}

      <CreateProductDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setIsEditMode(false);
          setSelectedProductForEdit(null);
        }}
        onSubmit={isEditMode ? handleEditSuccess : handleCreateProduct}
        corporateAccount={corporateAccount}
        projectId={currentProject}
        currentProject={currentProject}
        isEditMode={isEditMode}
        productToEdit={selectedProductForEdit}
        userId={userId}
      />

      <ManageUserAccessDialog
        isOpen={isUserAccessDialogOpen}
        onClose={() => {
          setIsUserAccessDialogOpen(false);
          setSelectedProductForAccess(null);
        }}
        corporateAccount={corporateAccount}
        projectId={currentProject}
        userId={userId}
        productId={selectedProductForAccess?.product_id}
        productName={selectedProductForAccess?.product_name}
        canManageUserAccess={permissions.canManageUserAccess}
        canRemoveUserAccess={permissions.canRemoveUserAccess }

      />

      {/* Delete Confirmation Dialog */}
      {renderDeleteConfirmDialog()}
    </div>
  );
};

export default Products;